import { formatDate } from '@frontend/mwm-ui'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import type { ReactElement } from 'react'

import { getNotionPage } from '~/api/notion'
import ChangelogHeader from '~/assets/changelog-header.png'
import ChangeLogModal from '~/components/ChangeLogModal'
import SkeletonLoader from '~/components/SkeletonLoader'
import ViewTitle from '~/components/ViewTitle'
import CHANGELOG from '~/const/changelog.json'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { formatNotionUrl } from '~/utils/notion'

import './styles.scss'

const changelog: Record<string, string> = CHANGELOG

interface ReleaseCardProps {
  onClick: VoidFunction
  version: string
}

const ReleaseCard = ({ onClick, version }: ReleaseCardProps): ReactElement => {
  const { data, isLoading } = useQuery({
    queryFn: () => getNotionPage(formatNotionUrl(changelog[version])),
    queryKey: ['changelog', version],
  })

  // @ts-expect-error object data is of type unknown
  const content = data?.results[0]?.paragraph?.rich_text?.[0]?.text?.content || 'No summary'
  // @ts-expect-error object data is of type unknown
  const createdAt = data?.results[0]?.created_time

  return (
    <div className="release-card" onClick={onClick}>
      <div className="release-card__header">
        <img src={ChangelogHeader} />
        <div className="release-card__header__version">v{version}</div>
      </div>
      <div className="release-card__content">
        {isLoading ? (
          <SkeletonLoader />
        ) : (
          <div className="release-card__content__text">
            <p>{content}</p>
            <span className="release-card__content__create-date">{formatDate(createdAt)}</span>
          </div>
        )}
      </div>
    </div>
  )
}

const Releases = (): ReactElement => {
  /** Local state */

  const [selectedVersion, setSelectedVersion] = useState('')

  /** Effects */

  useDocumentTitle('Releases')

  /** Handlers */

  const handleClick = (version: string) => () => setSelectedVersion(version)

  const handleClose = () => setSelectedVersion('')

  return (
    <>
      <ViewTitle>Releases</ViewTitle>
      <div className="releases-view">
        {Object.keys(changelog)
          .reverse()
          .map((version) => (
            <ReleaseCard key={version} onClick={handleClick(version)} version={version} />
          ))}
      </div>
      <ChangeLogModal
        isOpen={!!selectedVersion}
        onClose={handleClose}
        pageId={!!selectedVersion ? formatNotionUrl(changelog[selectedVersion]) : ''}
        version={selectedVersion}
      />
    </>
  )
}

export default Releases
